/**
 * Маршрутизация SPA «Элеонора»: единственное место, где меняется адрес страницы.
 *
 * Экраны выбираются по хешу (`#/register`, `#/login`, `#/application`, `#/organizer`,
 * `#/admin`) и по одноразовой ссылке из письма (`?token=…`). Саму отрисовку выполняет
 * точка входа `main.ts` — функция `start()`, зарегистрированная здесь через
 * `setRouteRenderer`. Адреса экранов и правила «роль → экран» заданы константами и
 * функциями этого модуля ниже.
 *
 * Зачем отдельный модуль. Присваивание `window.location.hash` не бросает
 * `hashchange`, если новый адрес совпадает с текущим, и экран молча не
 * перерисовывается: так «не работали» пункт «Выйти» в меню мастхеда и ссылка
 * «Перейти ко входу» на экране подтверждения email. Здесь оба случая — адрес
 * изменился и адрес совпал — идут через `navigate()`, поэтому переход срабатывает
 * всегда, а `syncAddress()` держит адресную строку в соответствии с показанным
 * экраном.
 */
import { primaryRole, sortRoles } from '../shared/roles';
import type { UserRole } from '../shared/types';

/** Отрисовка текущего маршрута: её регистрирует `main.ts` при старте SPA. */
let renderRoute: (() => void) | null = null;

/** Регистрирует отрисовку маршрута (вызывается один раз, до первого перехода). */
export function setRouteRenderer(render: () => void): void {
  renderRoute = render;
}

/**
 * Переходит на маршрут: меняет адрес страницы и показывает соответствующий экран.
 * Обычный случай — смена хеша, её подхватывает `hashchange` (слушатель в `main.ts`).
 * Если адрес уже такой (выход из аккаунта на экране с хешем `#/login`), события не
 * будет, поэтому экран рисуется напрямую.
 */
export function navigate(href: string): void {
  if (window.location.hash === href) {
    renderRoute?.();
    return;
  }
  window.location.hash = href;
}

/**
 * Токен восстановления пароля из строки запроса адреса (`?reset=…`, задача 28).
 * Отдельной функцией, а не разбором внутри экрана: одноразовая ссылка из письма —
 * часть маршрутизации (`start()` в `main.ts` разбирает её первой), и чистую функцию
 * можно проверить тестом без DOM (`tests/frontend/router.test.ts`).
 */
export function resetTokenFromSearch(search: string): string | null {
  const token = new URLSearchParams(search).get('reset');
  return token ? token : null;
}

/**
 * Приводит адрес к текущему экрану без записи в историю и без перерисовки: подставляет
 * хеш показанного экрана и отбрасывает параметры запроса, чтобы одноразовая ссылка
 * `?token=…` из письма не «залипала» и не перехватывала переходы (её перехватывает
 * `start()` — приоритет подтверждения email выше маршрута). Собираем адрес из
 * `pathname` и хеша: относительная ссылка вида `#/login` унаследовала бы строку
 * запроса текущего адреса.
 */
export function syncAddress(href: string): void {
  if (window.location.hash === href && !window.location.search) return;
  history.replaceState(null, '', window.location.pathname + href);
}

/**
 * Адреса экранов SPA — одна точка правды: и `main.ts` (выбор экрана), и разметка
 * (ссылки) берут их отсюда. Экраны ролей: «Заявка» (`#/application`), «Организатор»
 * (`#/organizer`) и «Администратор» (`#/admin`) — задача 22.
 */
export const REGISTER_HREF = '#/register';
export const LOGIN_HREF = '#/login';
/** Экран «Забыли пароль?» — запрос ссылки восстановления (задача 28). */
export const FORGOT_PASSWORD_HREF = '#/forgot-password';
/** Домашний экран базовой роли: форма заявки. */
export const APPLICATION_HREF = '#/application';
/** Экран организатора — списки по подтверждённым заявкам (`src/frontend/organizerScreen.ts`). */
export const ORGANIZER_HREF = '#/organizer';
/** Экран «Администратор» — секции «Турнир», «Действия», «Пользователи» и «Аудит» (`src/frontend/adminScreen.ts`). */
export const ADMIN_HREF = '#/admin';
/**
 * Экран «Заявка команды» (задача 43) — заявка одной команды глазами организатора
 * (`src/frontend/organizerTeamScreen.ts`). Это **вложенный** маршрут раздела: адрес несёт
 * адрес команды (`ownerUserId`), поэтому ссылку можно открыть заново, обновить страницу и
 * переслать — заявка откроется та же. В домашних адресах ролей (`roleScreenHrefs`) он не
 * значится: разделу он не принадлежит, а показывается отдельным правилом в `main.ts`.
 */
export const ORGANIZER_TEAM_HREF = '#/organizer/team';

/** Имя query-параметра адреса команды — тот же адрес, что у строки таблиц раздела. */
const ORGANIZER_TEAM_OWNER_PARAM = 'ownerUserId';

/** Адрес экрана «Заявка команды» по адресу команды (владельцу заявки). */
export function organizerTeamHref(ownerUserId: string): string {
  return `${ORGANIZER_TEAM_HREF}?${ORGANIZER_TEAM_OWNER_PARAM}=${encodeURIComponent(ownerUserId)}`;
}

/**
 * Адрес команды из адреса экрана «Заявка команды», `null` — это не адрес команды
 * (другой маршрут, адрес команды не передан или передан пустым). Отдельная чистая
 * функция — по образцу `resetTokenFromSearch`: её проверяет тест без DOM, а `main.ts`
 * по её ответу выбирает, показывать ли заявку команды вместо домашнего экрана роли.
 */
export function organizerTeamOwnerUserId(href: string): string | null {
  const [path, search = ''] = href.split('?');
  if (path !== ORGANIZER_TEAM_HREF) return null;
  const ownerUserId = new URLSearchParams(search).get(ORGANIZER_TEAM_OWNER_PARAM);
  return ownerUserId ? ownerUserId : null;
}

/** Адрес экрана каждой роли. */
const ROLE_SCREEN_HREFS: Record<UserRole, string> = {
  organizer: ORGANIZER_HREF,
  administrator: ADMIN_HREF,
  user: APPLICATION_HREF,
};

/**
 * Домашний экран набора ролей — экран старшей роли (`primaryRole`): у организатора это
 * «Организатор», у остальных — «Заявка». Показывается после входа и при попытке открыть
 * адрес экрана, который роли не принадлежит.
 */
export function homeHref(roles: UserRole[]): string {
  return ROLE_SCREEN_HREFS[primaryRole(roles)];
}

/**
 * Адреса экранов, доступных набору ролей, в порядке старшинства. `main.ts` сверяет с
 * ними маршрут из адресной строки: экран чужой роли не рисуется и при ручном вводе адреса
 * (права всё равно проверяет API — это защита от «пустого» экрана, а не от доступа).
 */
export function roleScreenHrefs(roles: UserRole[]): string[] {
  return sortRoles(roles).map(roleScreenHref);
}

/** Адрес экрана роли — по нему же строятся пункты меню аккаунта (`accountMenu.ts`). */
export function roleScreenHref(role: UserRole): string {
  return ROLE_SCREEN_HREFS[role];
}
